import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { signup } from "@/store/modules/signup";
import { addArtist } from "@/store/modules/addArtist";

Vue.use(Vuex);

Vue.config.devtools =
  (process.env.NODE_ENV && process.env.NODE_ENV === "development") || true;

const getInitialState = () => ({
  artist: {
    id: "",
    name: "",
    spotify_id: "",
    avatar_url: "",
    username: "",
    first_billing_date: "",
    next_billing_date: "",
    current_plan: "",
    active_plan: "",
    phone_numbers: [],
  },
  user: {
    id: "",
    email: "",
    plan: "",
    first_billing_date: "",
    next_billing_date: "",
    billing_interval: "",
    braintree_subscription_status: "",
    signup_finished: null,
    feature_flags: [],
    artists: [
      {
        id: "",
        name: "",
        avatar_url: "",
      },
    ],
  },
  billingUser: {
    id: "",
    email: "",
    braintree_subscription_status: "",
    first_billing_date: "",
    feature_flags: [],
  },
  addArtistWizardData: {
    image: null,
  },
  createLinkWizardData: {
    source_link: null,
    image: null,
    title: null,
    subtitle: null,
    description: null,
    preview_type: null,
    preview_url: null,
    preview_source: null,
    youtube_preview_url: null,
    custom_preview_url: null,
    layout: 2,
    theme: "light",
    display_artist_socials: true,
    domain: "vibe.to",
    source_destination: {
      name: "Spotify",
      url: null,
      type: "spotify",
    },
    destinations: [],
    preReleaseDestinations: [],
    tracks: [],
    release_time: null,
    offset: null,
    timezone: null,
    cover_image_style: null
  },
  editLinkWizardData: {
    image: null,
    destinations: [],
    preReleaseDestinations: [],
  },
  statsToday: {
    fans: 0,
    views: 0,
  },
  statsDaily: {},
  snackIsActive: false,
  snackText: null,
  snackType: "default",
  rid: null,
});

export default new Vuex.Store({
  modules: {
    signup,
    addArtist,
  },
  state: Object.assign({}, getInitialState()),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, getInitialState());
    },
    setArtist(state, artist) {
      state.artist = artist;
    },
    setArtists(state, artists) {
      state.user.artists = artists;
    },
    removeArtist(state, artist_id) {
      state.user.artists = state.user.artists.filter((x) => x.id !== artist_id);
    },
    setUser(state, user) {
      state.user = { ...state.user, ...user };
    },
    setArtistPhoneNumbers(state, phoneNumbers) {
      state.artist = { ...state.artist, phone_numbers: phoneNumbers };
      state.user.artists[0] = {
        ...state.user.artists[0],
        phone_numbers: phoneNumbers,
      };
    },
    setBillingUser(state, user) {
      state.billingUser = user;
    },
    setPlan(state, plan) {
      state.user.plan = plan;
    },
    setName(state, data) {
      state.user.first_name = data.first_name;
      state.user.last_name = data.last_name;
    },
    setEmail(state, email) {
      state.user.email = email;
    },
    setFirstBillingDate(state, firstBillingDate) {
      state.user.first_billing_date = firstBillingDate;
    },
    setNextBillingDate(state, nextBillingDate) {
      state.user.next_billing_date = nextBillingDate;
    },
    setBillingInterval(state, billingInterval) {
      state.user.billing_interval = billingInterval;
    },
    setSignupFinished(state, finished = true) {
      state.user.signup_finished = finished;
    },
    setStatsToday(state, statsToday) {
      state.statsToday = statsToday;
    },
    setStatsDaily(state, statsDaily) {
      state.statsDaily = statsDaily;
    },
    setSnack(state, { isActive, text, type }) {
      state.snackIsActive = isActive;
      state.snackText = text;
      state.snackType = type;
    },
    resetSnack(state) {
      state.snackIsActive = false;
      state.snackText = null;
      state.snackType = "default";
    },
    setCreateLinkWizardData(state, data) {
      state.createLinkWizardData = {
        ...state.createLinkWizardData,
        ...data,
      };
    },
    setCreateLinkWizardSourceLink(state, data) {
      state.createLinkWizardData.source_link = data;
    },
    setCreateLinkWizardTitle(state, data) {
      state.createLinkWizardData.title = data;
    },
    setCreateLinkWizardImage(state, data) {
      state.createLinkWizardData.image = data;
    },
    resetCreateLinkWizardData(state) {
      state.createLinkWizardData = {
        source_link: null,
        image: null,
        title: null,
        subtitle: null,
        description: null,
        preview_type: null,
        preview_url: null,
        youtube_preview_url: null,
        custom_preview_url: null,
        theme: "light",
        display_artist_socials: true,
        domain: "vibe.to",
        source_destination: {
          name: "Spotify",
          url: null,
          type: "spotify",
        },
        destinations: [],
        preReleaseDestinations: [],
        tracks: [],
        rid: null,
        release_time: null,
        offset: null,
        timezone: null,
        cover_image_style: null
      };
    },
    setEditLinkWizardData(state, data) {
      state.editLinkWizardData = {
        ...state.editLinkWizardData,
        ...data,
      };
    },
    setEditLinkWizardImage(state, data) {
      state.editLinkWizardData.image = data;
    },
    resetEditLinkWizardData(state) {
      state.editLinkWizardData = {
        image: null,
        preview_type: null,
        preview_url: null,
        youtube_preview_url: null,
        custom_preview_url: null,
        destinations: [],
        preReleaseDestinations: [],
        tracks: [],
        date: null,
        time: null,
        release_time: null,
        offset: null,
        timezone: null,
        post_release_title: null,
        post_release_subtitle: null,
        type: null,
        id: null,
        cover_image_style: null
      };
    },
    logout(state) {
      Object.assign(state, getInitialState());
    },
  },
  actions: {
    resetState({ commit }) {
      commit("RESET_STATE");
    },
    async setArtist({ commit }, artist) {
      commit("setArtist", artist);
    },
    setUser({ commit }, user) {
      commit("setUser", user);
    },
    setArtistPhoneNumbers({ commit }, phoneNumbers) {
      commit("setArtistPhoneNumbers", phoneNumbers);
    },
    setSignupFinished({ commit }, finished) {
      commit("setSignupFinished", finished);
    },
    setBillingUser({ commit }, user) {
      commit("setBillingUser", user);
    },
    setPlan({ commit }, plan) {
      commit("setPlan", plan);
    },
    setFirstBillingDate({ commit }, firstBillingDate) {
      commit("setFirstBillingDate", firstBillingDate);
    },
    setNextBillingDate({ commit }, nextBillingDate) {
      commit("setNextBillingDate", nextBillingDate);
    },
    setBillingInterval({ commit }, interval) {
      commit("setBillingInterval", interval);
    },
    setBillingStatus({ commit }, status) {
      commit("setBillingStatus", status);
    },
    setStatsToday({ commit }, statsToday) {
      commit("setStatsToday", statsToday);
    },
    setStatsDaily({ commit }, statsDaily) {
      commit("setStatsDaily", statsDaily);
    },
    async removeArtist({ commit }, artist_id) {
      commit("removeArtist", artist_id);
    },
    snack(
      { commit },
      {
        isActive = true,
        text = null,
        type = "default",
        autoDismiss = true,
        delay = 5000,
      }
    ) {
      commit("setSnack", { isActive, text, type });

      if (autoDismiss) {
        setTimeout(() => {
          commit("resetSnack");
        }, delay);
      }
    },
    logout({ commit }) {
      commit("logout");
      commit("signup/reset");
      commit("addArtist/reset");
    },
  },

  getters: {
    getArtist(state) {
      return state.artist;
    },
    getUser(state) {
      return state.user;
    },
    getBillingUser(state) {
      return state.billingUser;
    },
    getActivatedFeatures(state) {
      const combinedFeatures = [
        ...(state.billingUser?.feature_flags ?? []),
        ...(state.user?.feature_flags ?? []),
      ];
      return combinedFeatures.flatMap(({ feature, enabled }) =>
        enabled ? [feature] : []
      );
    },
    getState(state) {
      return state;
    },
    getStatsToday(state) {
      return state.statsToday;
    },
    getStatsDaily(state) {
      return state.statsDaily;
    },
    getViewsToday(state) {
      return state.statsToday.views;
    },
    getSnackIsActive(state) {
      return state.snackIsActive;
    },
    getSnackText(state) {
      return state.snackText;
    },
    getSnackType(state) {
      return state.snackType;
    },
    getCreateLinkWizardData(state) {
      return state.createLinkWizardData;
    },
    getEditLinkWizardData(state) {
      return state.editLinkWizardData;
    },
  },
  plugins: [createPersistedState()],
});
